// Shared response Zod schema for GET /api/v1/migration/summary — the migration
// continuity screen's "Here's where you left off" data. The migration Lambda
// serves it and the mobile client parses responses through the same schema, so
// the wire contract has one owner (ADR-066).
//
// Every portion is independently optional: a portion whose backing migrated
// data is absent is omitted from the response, never fabricated or defaulted.
// The client renders exactly the portions present and skips the screen when
// none is.

import { z } from 'zod';
import { apiEnvelope } from '../../envelope';

/** The member's main migrated programme, as display text served by the API. */
export const migrationSummaryFocusSchema = z.object({
  label: z.string().min(1),
});
export type MigrationSummaryFocus = z.infer<typeof migrationSummaryFocusSchema>;

/** The main programme's migrated goal, only when the Tier 0 transfer recorded one. */
export const migrationSummaryGoalSchema = z.object({
  label: z.string().min(1),
});
export type MigrationSummaryGoal = z.infer<typeof migrationSummaryGoalSchema>;

/**
 * The bound `days_of_history` keeps FOREVER. Every bundle already installed
 * parses this response with the copy of this schema compiled into it, and the
 * shipped ones bound that field at 30 — so widening it in place would make an
 * honest count unparseable on those binaries, not just unfamiliar. The real
 * total moved to `days_of_history_total` instead.
 */
export const MIGRATION_SUMMARY_MAX_DAYS = 30;

/**
 * Migrated check-in history, as distinct member-local days.
 *
 * Two fields, deliberately:
 * - `days_of_history` is the count clamped to `MIGRATION_SUMMARY_MAX_DAYS`. It
 *   exists to keep already-installed bundles parsing (ADR-135) and must never
 *   be widened — its bound is a shipped client's contract, not ours to change.
 * - `days_of_history_total` is the real, unbounded count. It is `.optional()`
 *   so a client built against it still parses a response from an older API that
 *   does not send it, and readers fall back to the clamped field.
 */
export const migrationSummaryCheckInsSchema = z.object({
  days_of_history: z.number().int().min(0).max(MIGRATION_SUMMARY_MAX_DAYS),
  days_of_history_total: z.number().int().min(0).optional(),
});
export type MigrationSummaryCheckIns = z.infer<typeof migrationSummaryCheckInsSchema>;

/** The display name of the member's migrated care specialist assignment. */
export const migrationSummaryCareSpecialistSchema = z.object({
  name: z.string().min(1),
});
export type MigrationSummaryCareSpecialist = z.infer<typeof migrationSummaryCareSpecialistSchema>;

// Deliberately NOT `.strict()` (ADR-135): the client parses responses through
// this schema, so a later slice adding a field must not break a shipped client.
export const migrationSummaryDataSchema = z.object({
  focus: migrationSummaryFocusSchema.optional(),
  goal: migrationSummaryGoalSchema.optional(),
  check_ins: migrationSummaryCheckInsSchema.optional(),
  care_specialist: migrationSummaryCareSpecialistSchema.optional(),
});
export type MigrationSummaryData = z.infer<typeof migrationSummaryDataSchema>;
export const migrationSummaryResponseSchema = apiEnvelope(migrationSummaryDataSchema);
